/**
 * VacationsPage — vacaciones del trabajador (art. 38 ET).
 *
 * Saldo del año (devengo - usado), solicitud por rango en días naturales
 * y listado con estado. La caducidad y el preaviso los gobierna el backend.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  requestVacation,
  getMyVacations,
  getVacationBalance,
  cancelVacation,
  VacationRequestItem,
  VacationBalance,
  apiMessage,
} from '../services/apiClient';
import { useAppStore } from '../store/useAppStore';

const STATUS_STYLES: Record<string, string> = {
  PENDING: 'bg-warn-soft text-warn',
  APPROVED: 'bg-brand-soft text-brand',
  REJECTED: 'bg-danger-soft text-danger',
  CANCELLED: 'bg-surface-2 text-ink-2',
};

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendiente',
  APPROVED: 'Aprobada',
  REJECTED: 'Rechazada',
  CANCELLED: 'Cancelada',
};

/** Hoy en el reloj del trabajador, no en UTC. */
function hoyLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

export function VacationsPage() {
  const user = useAppStore((s) => s.user);
  const year = new Date().getFullYear();

  const [balance, setBalance] = useState<VacationBalance | null>(null);
  const [requests, setRequests] = useState<VacationRequestItem[]>([]);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    const [bal, todas] = await Promise.all([
      getVacationBalance(user.id, year),
      // SIN `year`: el único filtro que acepta el endpoint es `charge_year`,
      // que el motor fija al año de INICIO del rango. Una solicitud pedida en
      // diciembre que empieza en enero cargaba al año siguiente y desaparecía
      // del historial en el mismo momento de crearla — "Sin solicitudes este
      // año" sobre una solicitud viva, pánico y duplicado (422 "solapa con la
      // solicitud N" que la propia pantalla decía que no existía). El backend
      // no permite filtrar por rango, así que la unión se hace en cliente
      // (ronda 3, r2b19): sin filtro el endpoint devuelve todo lo mío,
      // ordenado por inicio, y es un listado acotado al propio trabajador.
      getMyVacations(),
    ]);
    const desde = `${year}-01-01`;
    const hasta = `${year}-12-31`;
    const visibles = todas.filter(
      (r) =>
        // consume días del saldo de este año (aunque su rango caiga en otro), o
        r.charge_year === year ||
        // cubre algún día natural de este año (las que cruzan el 1 de enero), o
        (r.start_date <= hasta && r.end_date >= desde) ||
        // todavía no ha llegado: esconder una solicitud recién creada para el
        // año que viene es exactamente el pánico que esto arregla
        r.start_date > hasta,
    );
    setBalance(bal);
    setRequests(visibles);
  }, [user, year]);

  useEffect(() => {
    load().catch(() => setError('No se pudo cargar el saldo'));
  }, [load]);

  const submit = async () => {
    setError(null);
    setLoading(true);
    try {
      await requestVacation({ start_date: start, end_date: end });
      setStart('');
      setEnd('');
      await load();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      setError(detail ?? 'Error al solicitar');
    } finally {
      setLoading(false);
    }
  };

  const cancel = async (id: number) => {
    // Sin `catch`, un 422 del backend era una promesa rechazada y nada más: la
    // pantalla no cambiaba, el trabajador volvía a pulsar y seguía sin pasar
    // nada. El silencio ES el bug — parece que el botón está roto y no hay una
    // línea que lo diga.
    setError(null);
    try {
      await cancelVacation(id);
      await load();
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo cancelar la solicitud'));
    }
  };

  return (
    <div className="p-4 space-y-4">
      {/* Saldo */}
      {balance && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-surface rounded-xl p-4 text-center shadow-sm">
            <p className="text-2xl font-bold text-ink">
              {balance.entitled_days}
            </p>
            <p className="text-xs text-ink-2">Devengados {year}</p>
          </div>
          <div className="bg-surface rounded-xl p-4 text-center shadow-sm">
            <p className="text-2xl font-bold text-ink">
              {balance.used_days}
            </p>
            <p className="text-xs text-ink-2">Usados/reservados</p>
          </div>
          <div className="bg-surface rounded-xl p-4 text-center shadow-sm">
            <p className="text-2xl font-bold text-brand">{balance.available_days}</p>
            <p className="text-xs text-ink-2">Disponibles</p>
          </div>
        </div>
      )}
      {balance?.expires_on && (
        <p className="text-xs text-ink-2">
          Los días de {year} caducan el {balance.expires_on} (art. 38.3 ET)
        </p>
      )}

      {/* Solicitud */}
      <div className="bg-surface rounded-xl p-4 shadow-sm space-y-3">
        <h3 className="font-medium text-ink">Solicitar vacaciones</h3>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs text-ink-2">Desde</span>
            <input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="mt-1 w-full rounded-lg border-line-strong text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs text-ink-2">Hasta (incluido)</span>
            <input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="mt-1 w-full rounded-lg border-line-strong text-sm"
            />
          </label>
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
        <button
          onClick={submit}
          disabled={!start || !end || loading}
          className="w-full py-3 bg-brand text-white rounded-xl font-medium hover:bg-brand-2 disabled:opacity-40 transition-colors"
        >
          {loading ? 'Enviando…' : 'Solicitar'}
        </button>
        <p className="text-xs text-ink-3">
          Días naturales. Recuerda: las fechas deben conocerse con 2 meses de
          antelación (art. 38.3 ET).
        </p>
      </div>

      {/* Historial */}
      <div className="space-y-2">
        {requests.map((r) => (
          <div
            key={r.id}
            className="bg-surface rounded-xl p-4 shadow-sm flex items-center justify-between"
          >
            <div>
              <p className="font-medium text-ink text-sm">
                {r.start_date} → {r.end_date}
              </p>
              <p className="text-xs text-ink-2">
                {r.days_natural} días naturales · año {r.charge_year}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 rounded-full text-xs ${STATUS_STYLES[r.status]}`}>
                {STATUS_LABELS[r.status]}
              </span>
              {/* La fecha se comparaba con el ISO en UTC: en la península,
                  entre las 00:00 y las 02:00 locales, "hoy" en UTC es ayer,
                  así que el botón salía justo en la ventana en que el backend
                  lo rechaza por "vacaciones ya iniciadas". */}
              {(r.status === 'PENDING' ||
                (r.status === 'APPROVED' && r.start_date > hoyLocal())) && (
                <button
                  onClick={() => cancel(r.id)}
                  className="text-xs text-danger"
                >
                  Cancelar
                </button>
              )}
            </div>
          </div>
        ))}
        {requests.length === 0 && (
          <p className="text-center text-sm text-ink-3 py-6">Sin solicitudes todavía</p>
        )}
      </div>
    </div>
  );
}
